/**
 * parentHomeViewModel — pure `(reduxState, studentId) → page props` for parent home.
 *
 * Lives: every upcoming occurrence, sorted, all days confounded — minus the ones where an absence is
 * reported and the finished ones, which live in « Tous mes lives » (see `homeSessions`).
 * Todos: the three legacy banners + the makeup button + the payment badge, plus one line per pending
 * « garder son tuteur » choice (`state.data.tutorSlotMoves`) — `parentTodoAdapter`.
 * Last report: page 1 of `state.data.liveReports.liveReports` (`lastReportAdapter`).
 * Tutors: `formData.user.live_sessions[].tutor` (`tutorListAdapter`) — consumed by the gestures.
 * Programs: `formData.user.current_study_programs` summaries.
 * Assessments: `schoolAssessments.byStudentId[studentId]`.
 * Chargement : voir `homeLoading` — le squelette la première fois seulement, par enfant.
 *
 * Mémoïsé (`createSelector`) sur les tranches qu'il lit : les onglets parent restent montés, donc
 * sans cela chaque action du store rejouerait tris, jointures et adaptateurs des quatre onglets.
 */
import moment from "moment"
import { createSelector } from "reselect"

import { cleanMediaUrl } from "../../../../../data/constants/apiConstants"
import {
    toUILiveSessions,
    type ApiLiveOccurrence,
    type ApiLiveSessionNested,
    type ApiStudentForSubjects,
} from "../../../adapters/parent/LiveSessions/liveSessionListAdapter"
import { toUIParentLastReport } from "../../../adapters/parent/ParentHome/lastReportAdapter"
import { type ApiLiveReportListItem } from "../../../adapters/parent/LiveReports/liveReportListAdapter"
import { toUIParentTodos, type ApiTodoUser } from "../../../adapters/parent/ParentHome/parentTodoAdapter"
import { toUIParentStageOffer, type ApiStageOffer } from "../../../adapters/parent/ParentHome/stageOfferAdapter"
import { toUIParentTutorChoices } from "../../../adapters/parent/ParentHome/tutorListAdapter"
import {
    toUIRevisionProgramSummaries,
    type ApiStageProgramRef,
} from "../../../adapters/parent/RevisionProgram/revisionProgramAdapter"
import { toUISchoolAssessments } from "../../../adapters/parent/SchoolAssessments/schoolAssessmentListAdapter"
import { toUISectionedChapters } from "../../../adapters/parent/SchoolAssessments/sectionedChapterAdapter"
import { toUISubject, type ApiSchoolSubject } from "../../../adapters/parent/shared/schoolSubjectAdapter"
import { isAbsenceReported, isSessionFinished } from "../../../ui/formatters/liveSessionFormatter"
import {
    type UILiveSession,
    type UIParentLastReport,
    type UIParentStageOffer,
    type UIParentTodo,
    type UIParentTutorChoice,
    type UIRevisionProgram,
    type UISchoolAssessment,
    type UISectionedChapter,
    type UISubject,
} from "../../../ui/types"
import { identityLoading, pricingFailed, sameAccount, type ApiSlice } from "../shared/identityLoading"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

/**
 * Ce que l'accueil montre : la prochaine en grand (`<NextSessionSection>`) + deux lignes dans
 * « À venir » (`<UpcomingSessionsSection>`). Au-delà, la réponse est « Voir plus » — c'est ce
 * que `hasMoreLives` annonce. Le seul endroit où ce budget s'écrit : gabarit et page lisent.
 */
const HOME_VISIBLE_SESSIONS = 3

export type ParentHomeViewModel = {
    /** L'enfant dont l'accueil parle — titre de la page. */
    child: { firstname: string; avatarUrl?: string } | null
    todos: UIParentTodo[]
    /** Le stage de vacances en vente pour cet enfant — `null` quand il n'y a rien à lui proposer. */
    stageOffer: UIParentStageOffer | null
    /**
     * Les séances à venir, triées, tous jours confondus, SANS celles où l'absence est signalée ni
     * celles qui sont terminées (`homeSessions`) — toutes : les gestes y choisissent. La première
     * est « Prochaine séance ».
     */
    nextSessions: UILiveSession[]
    /** Les lignes de « À venir », déjà coupées : celles qui suivent la prochaine, deux au plus. */
    upcomingSessions: UILiveSession[]
    /** « Tous mes lives » a plus à montrer que l'accueil — pilote « Voir plus ». */
    hasMoreLives: boolean
    /**
     * L'enfant a au moins une séance à venir — absence signalée ou suspension pour paiement
     * comprises : sur une séance suspendue, le geste mène à « Régulariser ». Sans elle, les tuiles
     * des gestes d'une séance (`PARENT_GESTURES.*.needsSession`) restent à leur place, inactives.
     */
    hasUpcomingSession: boolean
    lastReport: UIParentLastReport | null
    /** Les tuteurs de l'élève, pour l'étape « Quel tuteur ? » des gestes. */
    tutors: UIParentTutorChoice[]
    revisionPrograms: UIRevisionProgram[]
    assessments: UISchoolAssessment[]
    subjects: UISubject[]
    chapters: UISectionedChapter[]
    /**
     * Rien à montrer encore pour CET enfant — la page montre son squelette (`homeLoading`). Un
     * rechargement des données d'un enfant déjà affiché ne le rallume pas : le contenu reste.
     */
    loading: boolean
    error: unknown | null
    /** formData.user email when identity matches — for AsParent lives fetch / list nav. */
    studentEmail: string | null
    studentId: string | number | null
    /** True when formData.user matches currentUser (safe to render sections / fire student-scoped APIs). */
    identityReady: boolean
    /**
     * `currentUser.payment_url` — le lien de recouvrement Stripe. C'est le MÊME qui porte la ligne
     * « À faire » (via l'adaptateur) et le dialogue d'une séance suspendue : lu ici une fois, il
     * ne peut pas diverger entre les deux. `null` tant que la tranche ne l'a pas reçu
     * (`TRANSITION.md`, § Accueil — risque 1).
     */
    paymentUrl: string | null
}

/**
 * Toutes les occurrences à venir, triées par date. Le legacy n'en gardait que le premier jour
 * (`firstDayOccurrences`) ; la carte d'accueil montre les trois prochaines séances tous jours
 * confondus. Divergence assumée, inscrite dans `TRANSITION.md`.
 */
function sortedUpcoming(occurrences: ApiLiveOccurrence[]): ApiLiveOccurrence[] {
    return [...occurrences].sort((a, b) => moment(a.date).diff(moment(b.date)))
}

/**
 * Ce que l'accueil montre des séances à venir, et s'il en reste pour « Voir plus ».
 *
 * Une séance où l'absence est signalée n'est PAS sur l'accueil (Wajih, 28/09 au soir, avec
 * Nawfal) : ni « Prochaine séance » — la prochaine est alors la suivante —, ni « À venir ». Elle
 * reste dans « Tous mes lives », grisée, et c'est de là que part « Annuler l'absence ». C'est la
 * règle du legacy (`firstDayOccurrences` écartait déjà ces séances).
 *
 * Une séance TERMINÉE n'y est pas non plus (Wajih, 01/10) : la carte « Prochaine séance » montrait
 * une séance du jour déjà clôturée. Même mécanique — la prochaine est la suivante, et la séance
 * reste dans « Tous mes lives », estompée, sous son badge « Terminé ».
 *
 * « Voir plus » compte TOUTES les séances contre celles que l'accueil montre, comme le legacy
 * (`hasMoreOccurrences`) : une séance écartée est une séance de plus dans « Tous mes lives », et
 * le lien doit y mener — pour une absence, c'est le seul chemin vers son annulation.
 */
function homeSessions(
    upcoming: UILiveSession[]
): Pick<ParentHomeViewModel, "nextSessions" | "upcomingSessions" | "hasMoreLives"> {
    const nextSessions = upcoming.filter((session) => !isAbsenceReported(session) && !isSessionFinished(session))
    const shown = Math.min(nextSessions.length, HOME_VISIBLE_SESSIONS)
    return {
        nextSessions,
        upcomingSessions: nextSessions.slice(1, HOME_VISIBLE_SESSIONS),
        hasMoreLives: upcoming.length > shown,
    }
}

/**
 * L'accueil charge-t-il encore pour CET enfant ? — le squelette la première fois seulement.
 *
 * Trois lectures font la page d'un enfant, et la page attend les trois avant de se montrer, pour
 * que rien ne saute à leur arrivée :
 *  - **l'identité** (`identityLoading`, la règle commune avec l'Abonnement) ;
 *  - **ses séances** : la bascule vide `live_occurrences`, et la lecture ne part qu'une fois
 *    l'identité réconciliée. Tant que la tranche parle d'un autre enfant (la réponse de l'enfant
 *    d'avant y reste), ou que sa première lecture est en vol sans rien d'arrivé, elles manquent ;
 *  - **le dernier bilan** : la bascule vide aussi les rapports ; en vol sans aucune ligne, il manque.
 *
 * Une fois l'enfant affiché, un rechargement (tarif, séances, rapports) ne rallume rien : le
 * contenu reste à l'écran. Et un échec de `getSubscribePricing` n'est jamais masqué par le
 * squelette : il rend la main à l'erreur, qui porte le « Réessayer ».
 */
export function homeLoading(input: {
    pricing: ApiSlice | undefined
    formUserId: string | number | null | undefined
    currentUserId: string | number | null | undefined
    studentEmail: string | null
    lives: ApiSlice | undefined
    hasOccurrences: boolean
    lastReports: ApiSlice | undefined
    hasReports: boolean
}): boolean {
    const { pricing, formUserId, currentUserId, studentEmail, lives, hasOccurrences } = input
    if (!sameAccount(formUserId, currentUserId)) return identityLoading(input)
    if (pricingFailed(pricing)) return false

    const livesOfAnotherChild = studentEmail != null && lives?.urlParams?.student_email !== studentEmail
    const livesFirstRead = !!lives?.request && !hasOccurrences
    const lastReportFirstRead = !!input.lastReports?.request && !input.hasReports

    return livesOfAnotherChild || livesFirstRead || lastReportFirstRead
}

/** Redux root is still untyped in this app — keep the VM boundary explicit. */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

type FormUser =
    | (ApiStudentForSubjects &
          ApiTodoUser & {
              id?: string | number
              firstname?: string
              avatar?: string | null
              current_study_programs?: unknown[]
              live_sessions?: ApiLiveSessionNested[]
              email?: string
              stage_offer?: ApiStageOffer | null
              /** The bought stage programme (`UserSubscriptionResource::stageProgram()`). */
              stage_program?: ApiStageProgramRef | null
          })
    | null

/**
 * `homeLoading` réduit à son booléen, et c'est lui — pas les tranches qu'il lit — qui entre dans le
 * view-model : un REQUEST ou un IDLE de la pagination des Rapports change `getStudentLiveReports`
 * sans changer le chargement, et l'accueil, monté caché, n'a pas à se recalculer pour si peu.
 */
export const selectHomeLoading = createSelector(
    [
        (state: ReduxState) => state?.api?.getSubscribePricing as ApiSlice | undefined,
        (state: ReduxState) => state?.data?.formData?.user?.id as string | number | undefined,
        (state: ReduxState) => selectDisplayedStudent(state)?.id as string | number | undefined,
        (state: ReduxState) => state?.data?.formData?.user?.email as string | undefined,
        (state: ReduxState) => state?.api?.getAllMyNextLiveSessionsAsParent as ApiSlice | undefined,
        (state: ReduxState) => state?.api?.getAllMyNextLiveSessions as ApiSlice | undefined,
        (state: ReduxState) => state?.api?.getStudentLiveReports as ApiSlice | undefined,
        (state: ReduxState) => (state?.data?.liveSessions?.live_occurrences ?? []).length > 0,
        (state: ReduxState) => (state?.data?.liveReports?.liveReports ?? []).length > 0,
    ],
    (
        pricing,
        formUserId,
        currentUserId,
        formUserEmail,
        livesAsParent,
        lives,
        lastReports,
        hasOccurrences,
        hasReports
    ) => {
        const studentEmail = sameAccount(formUserId, currentUserId) ? (formUserEmail ?? null) : null
        return homeLoading({
            pricing,
            formUserId,
            currentUserId,
            studentEmail,
            // La tranche que `useParentHomeData` fait partir : « AsParent » dès que l'e-mail est connu.
            lives: studentEmail ? livesAsParent : lives,
            hasOccurrences,
            lastReports,
            hasReports,
        })
    }
)

/**
 * Les deux lectures en route que l'accueil montre (la ligne des contrôles, les chapitres du
 * formulaire), lues HORS du view-model : leur REQUEST et leur retour ne changent aucune donnée,
 * et recalculeraient tout l'accueil pour un booléen. L'écran les combine avec le view-model.
 */
export const selectAssessmentsRequesting = (state: ReduxState): boolean => !!state?.api?.getSchoolAssessments?.request
export const selectChaptersRequesting = (state: ReduxState): boolean =>
    !!state?.api?.getChaptersBySchoolSubjectId?.request

/**
 * La PREMIÈRE lecture des contrôles de cet enfant est en route — ils arrivent après le reste de
 * l'accueil, et « Aucun contrôle à venir » serait faux d'ici là. Une relecture ne la rallume pas.
 */
export function homeAssessmentsLoading(
    vm: Pick<ParentHomeViewModel, "identityReady" | "assessments">,
    assessmentsRequesting: boolean
): boolean {
    return vm.identityReady && assessmentsRequesting && vm.assessments.length === 0
}

export const parentHomeViewModel = createSelector(
    [
        (state: ReduxState) => selectDisplayedStudent(state) as (ApiStudentForSubjects & ApiTodoUser) | null,
        (state: ReduxState) => state?.data?.formData?.user as FormUser,
        (state: ReduxState) => state?.api?.getSubscribePricing,
        (state: ReduxState) => state?.data?.liveSessions?.live_occurrences as ApiLiveOccurrence[] | undefined,
        (state: ReduxState) => state?.data?.staticData?.school_subjects as ApiSchoolSubject[] | undefined,
        (state: ReduxState) => state?.data?.schoolAssessments?.byStudentId,
        (state: ReduxState) => state?.data?.liveReports?.liveReports as ApiLiveReportListItem[] | undefined,
        (state: ReduxState) => state?.data?.staticData?.summer_config,
        (state: ReduxState) => state?.data?.liveSessions?.attendance_flags_to_be_recovered,
        (state: ReduxState) => state?.data?.tutorSlotMoves?.moves,
        (state: ReduxState) => state?.data?.chapters?.chaptersList,
        selectHomeLoading,
        (_state: ReduxState, selectedUserId: string | number | null) => selectedUserId,
    ],
    (
        currentUser,
        formUser,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        pricingReq: any,
        occurrencesRaw,
        subjectsRawOrNil,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        assessmentsByStudentId: any,
        liveReportsRaw,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        summerConfig: any,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        attendanceFlagsToBeRecovered: any,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        tutorSlotMoves: any,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        chaptersList: any,
        loading,
        selectedUserId
    ): ParentHomeViewModel => {
        const identityReady = sameAccount(formUser?.id, currentUser?.id)
        const studentId = identityReady ? (formUser?.id ?? null) : null
        const studentEmail = identityReady ? (formUser?.email ?? null) : null

        const occurrences = occurrencesRaw ?? []
        const error = !loading ? (pricingReq?.error ?? null) : null
        const subjectsRaw = subjectsRawOrNil ?? []
        const assessmentStudentId = selectedUserId ?? studentId
        const assessmentsRaw = assessmentStudentId != null ? (assessmentsByStudentId?.[assessmentStudentId] ?? []) : []

        const studentForSubjects = (identityReady ? formUser : currentUser) as ApiStudentForSubjects | null
        const upcoming = toUILiveSessions(sortedUpcoming(occurrences), {
            student: studentForSubjects,
            apiSubjects: subjectsRaw,
            paymentBlocked: !!studentForSubjects?.is_payment_blocked,
        })
        const { nextSessions, upcomingSessions, hasMoreLives } = homeSessions(upcoming)

        // Le nom et la photo de l'enfant affiché : `formData.user` tant que l'identité est réconciliée
        // (une chip qui bascule fait DEVENIR le compte connecté cet enfant, cf. `useParentHomeData`).
        // `cleanMediaUrl` réécrit le domaine de dev — l'avatar d'un élève est UNE url (`user.avatar`).
        const childFirstname = identityReady ? (formUser?.firstname ?? "") : ""
        const child =
            childFirstname !== ""
                ? { firstname: childFirstname, avatarUrl: cleanMediaUrl(formUser?.avatar) ?? undefined }
                : null

        return {
            child,
            todos: identityReady
                ? toUIParentTodos({
                      student: formUser,
                      currentUser,
                      summerConfig,
                      attendanceFlagsToBeRecovered,
                      tutorSlotMoves,
                      childFirstname,
                  })
                : [],
            stageOffer: identityReady ? toUIParentStageOffer(formUser?.stage_offer) : null,
            nextSessions,
            upcomingSessions,
            hasMoreLives,
            hasUpcomingSession: upcoming.length > 0,
            lastReport: identityReady ? toUIParentLastReport(liveReportsRaw ?? [], subjectsRaw) : null,
            tutors: identityReady
                ? toUIParentTutorChoices(formUser?.live_sessions, {
                      student: studentForSubjects,
                      apiSubjects: subjectsRaw,
                  })
                : [],
            revisionPrograms: identityReady
                ? toUIRevisionProgramSummaries(
                      (formUser?.current_study_programs ?? []) as Parameters<typeof toUIRevisionProgramSummaries>[0],
                      formUser?.stage_program
                  )
                : [],
            assessments: identityReady ? toUISchoolAssessments(assessmentsRaw) : [],
            subjects: subjectsRaw.map((s) => toUISubject(s)),
            chapters: toUISectionedChapters(chaptersList),
            loading,
            error,
            studentEmail,
            studentId,
            identityReady,
            paymentUrl: currentUser?.payment_url ?? null,
        }
    }
)
